import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { blogPosts } from '../../utils/blogData';
import Icon from '../../components/AppIcon';
import { motion, useScroll, useSpring } from 'framer-motion';

const SingleBlogPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [readingProgress, setReadingProgress] = useState(0);
  const [isCopied, setIsCopied] = useState(false);
  
  const handleShare = async () => {
    const shareData = {
      title: post.title,
      text: post.excerpt,
      url: window.location.href,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        copyToClipboard();
      }
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };
  
  const post = useMemo(() => blogPosts.find((p) => p.slug === slug), [slug]);
  
  const relatedPosts = useMemo(() => {
    if (!post) return [];
    return blogPosts
      .filter(p => p.id !== post.id && (p.category === post.category || Math.random() > 0.5))
      .slice(0, 3);
  }, [post]);

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (!post) {
      navigate('/blog');
    }
  }, [post, navigate]);

  if (!post) return null;

  return (
    <>
      <Helmet>
        <title>{post.title} | Dishant Madariya Blog</title>
        <meta name="description" content={post.excerpt} />
        <meta name="keywords" content={post.keywords} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={post.image} />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:author" content={post.author} />
      </Helmet>

      {/* Reading Progress Bar */}
      <motion.div 
        className="fixed top-0 left-0 right-0 h-1.5 bg-primary origin-left z-[100]"
        style={{ scaleX }}
      />

      <div className="min-h-screen bg-background pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          
          <div className="flex flex-col lg:flex-row gap-16">
            
            {/* Sidebar (Desktop) */}
            <aside className="hidden lg:block w-16 sticky top-40 h-fit">
              <div className="flex flex-col items-center space-y-6">
                <div className="w-px h-12 bg-border" />
                {/* <a 
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-surface border border-border flex items-center justify-center text-text-secondary hover:text-primary hover:border-primary transition-all duration-300 group"
                >
                  <Icon name="Linkedin" size={20} />
                </a> */}
                <a 
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-surface border border-border flex items-center justify-center text-text-secondary hover:text-primary hover:border-primary transition-all duration-300"
                >
                  <Icon name="Twitter" size={20} />
                </a>
                <button 
                  onClick={handleShare}
                  className="w-12 h-12 rounded-full bg-surface border border-border flex items-center justify-center text-text-secondary hover:text-primary hover:border-primary transition-all duration-300 relative"
                >
                  <Icon name={isCopied ? "Check" : "Share2"} size={20} />
                  {isCopied && (
                    <span className="absolute -top-10 left-1/2 -translate-x-1/2 px-2 py-1 bg-primary text-white text-[10px] rounded font-bold whitespace-nowrap">
                      Link Copied!
                    </span>
                  )}
                </button>
                <div className="w-px h-12 bg-border" />
              </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 max-w-4xl mx-auto">
              
              {/* Breadcrumbs */}
              <nav className="flex items-center space-x-2 text-sm text-text-secondary mb-8">
                <Link to="/blog" className="hover:text-primary transition-colors">Blog</Link>
                <Icon name="ChevronRight" size={14} />
                <span className="text-primary font-medium">{post.category}</span>
              </nav>

              {/* Header */}
              <header className="mb-12">
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/10 rounded-full text-primary font-bold text-xs uppercase tracking-widest mb-6"
                >
                  <Icon name="Tag" size={12} />
                  <span>{post.category}</span>
                  <span className="text-text-secondary mx-1">•</span>
                  <span>{post.readTime}</span>
                </motion.div>

                <motion.h1 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="text-3xl md:text-4xl lg:text-5xl font-black text-text-primary leading-tight mb-8"
                >
                  {post.title}
                </motion.h1>

                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="flex flex-wrap items-center gap-6 py-6 border-y border-border"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center text-white shadow-lg shadow-primary/20">
                      <Icon name="User" size={20} />
                    </div>
                    <div>
                      <p className="text-text-primary font-bold">{post.author}</p>
                      <p className="text-text-secondary text-xs">Technical Writer & Developer</p>
                    </div>
                  </div>
                  <div className="h-6 w-px bg-border hidden sm:block" />
                  <div className="flex flex-col">
                    <span className="text-text-secondary text-[10px] font-bold uppercase tracking-wider">Published on</span>
                    <span className="text-text-primary font-medium text-sm">{post.date}</span>
                  </div>
                </motion.div>
              </header>

              {/* Featured Image */}
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="relative aspect-[21/9] rounded-2xl overflow-hidden mb-12 shadow-xl border border-border"
              >
                <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
              </motion.div>

              {/* Article Content */}
              <article 
                className="prose prose-base md:prose-lg dark:prose-invert max-w-none 
                prose-headings:text-text-primary prose-headings:font-black
                prose-p:text-text-secondary prose-p:leading-relaxed
                prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:bg-surface prose-blockquote:p-6 prose-blockquote:rounded-r-lg prose-blockquote:italic
                prose-strong:text-text-primary prose-strong:font-bold
                prose-code:text-primary prose-code:bg-primary/5 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-md
                prose-img:rounded-xl prose-img:shadow-lg prose-img:border prose-img:border-border"
              >
                <div dangerouslySetInnerHTML={{ __html: post.content }} />
              </article>

              {/* Footer / Share (Mobile) */}
              <div className="mt-20 lg:hidden flex justify-center space-x-4">
                <a 
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-surface border border-border flex items-center justify-center text-text-secondary"
                >
                  <Icon name="Linkedin" size={20} />
                </a>
                <a 
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-surface border border-border flex items-center justify-center text-text-secondary"
                >
                  <Icon name="Twitter" size={20} />
                </a>
                <button 
                  onClick={handleShare}
                  className="w-12 h-12 rounded-full bg-surface border border-border flex items-center justify-center text-text-secondary relative"
                >
                  <Icon name={isCopied ? "Check" : "Share2"} size={20} />
                </button>
              </div>

              {/* Related Posts */}
              <section className="mt-32">
                <div className="flex items-center justify-between mb-10">
                  <h3 className="text-3xl font-black text-text-primary">Continue Reading</h3>
                  <Link to="/blog" className="text-primary font-bold hover:underline flex items-center space-x-2">
                    <span>View all</span>
                    <Icon name="ArrowRight" size={16} />
                  </Link>
                </div>
                <div className="grid md:grid-cols-3 gap-8">
                  {relatedPosts.map((related) => (
                    <Link 
                      key={related.id} 
                      to={`/blog/${related.slug}`}
                      className="group block bg-surface border border-border rounded-2xl overflow-hidden hover:border-primary/50 transition-all duration-300 hover:shadow-xl"
                    >
                      <div className="aspect-video overflow-hidden">
                        <img src={related.image} alt={related.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                      </div>
                      <div className="p-6">
                        <span className="text-xs font-bold text-primary mb-2 block uppercase tracking-tighter">{related.category}</span>
                        <h4 className="font-bold text-text-primary group-hover:text-primary transition-colors line-clamp-2 mb-2">
                          {related.title}
                        </h4>
                        <div className="flex items-center justify-between text-xs text-text-secondary mt-4">
                          <span>{related.date}</span>
                          <span>{related.readTime}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>

              {/* Newsletter / CTA */}
              <section className="mt-32 p-10 md:p-16 rounded-[2rem] bg-gradient-to-br from-primary to-blue-700 text-white relative overflow-hidden shadow-2xl shadow-primary/30">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
                <div className="relative z-10 max-w-xl">
                  <h3 className="text-3xl md:text-4xl font-black mb-6">Stay ahead of the curve</h3>
                  <p className="text-blue-100 text-lg mb-10 leading-relaxed">
                    Get my latest articles on development, architecture, and tech insights delivered straight to your inbox. No spam, ever.
                  </p>
                  <form className="flex flex-col sm:flex-row gap-4" onSubmit={(e) => e.preventDefault()}>
                    <input 
                      type="email" 
                      placeholder="Enter your email" 
                      className="flex-1 bg-white/10 border border-white/20 rounded-2xl px-6 py-4 text-white placeholder:text-blue-200 outline-none focus:bg-white/20 transition-all"
                    />
                    <button className="px-8 py-4 bg-white text-primary font-bold rounded-2xl hover:bg-blue-50 transition-colors shadow-lg">
                      Subscribe
                    </button>
                  </form>
                </div>
              </section>

            </main>
          </div>
        </div>
      </div>
    </>
  );
};

export default SingleBlogPage;
